import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { RefreshCw, Loader2 } from "lucide-react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { EvaluationCriteriaExtraction } from "@/components/extraction/EvaluationCriteriaExtraction"
import { ComplianceMatrixExtraction } from "@/components/extraction/ComplianceMatrixExtraction"
import { SubmissionChecklistExtraction } from "@/components/extraction/SubmissionChecklistExtraction"
import { KeyDatesExtraction } from "@/components/extraction/KeyDatesExtraction"
import { RiskRegisterExtraction } from "@/components/extraction/RiskRegisterExtraction"
import { TenderOverviewView } from "@/components/extraction/TenderOverviewView"
import { AddendumReviewView } from "@/components/extraction/AddendumReviewView"
import { computeStaleCategories, isRanAndEmpty, useExtractionStore } from "@/stores/extraction-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { useReconciliationStore } from "@/stores/reconciliation-store"
import { addendumNeedsReconciliation, type ReconciliationRunSummary } from "@/lib/schemas"
import { EXTRACTION_CATEGORY_LABELS, type ExtractionCategory } from "@/types/extraction"
import { ChatDocumentPanel } from "@/components/chat/ChatDocumentPanel"
import { useDockHost, useDockedAnalysisPreview } from "@/hooks/useDockedPreview"
import { usePreviewStore } from "@/stores/preview-store"
import { toastApiError } from "@/lib/api"
import { cn } from "@/lib/utils"
import { NOTICE_ACT, NOTICE_QUIET } from "@/lib/notice-tier"

/** Humanized label for the category the reconciliation worker is currently on. */
function latestReconcileCategoryLabel(
  counts: ReconciliationRunSummary["counts"]
): string | null {
  const perCategory = counts?.["perCategory"]
  if (!perCategory || typeof perCategory !== "object") return null
  const keys = Object.keys(perCategory as Record<string, unknown>)
  const last = keys[keys.length - 1]
  if (!last) return null
  return EXTRACTION_CATEGORY_LABELS[last as ExtractionCategory] ?? last
}

/** All possible sub-tab values under the Analysis tab */
export type AnalysisView =
  | "tender_overview"
  | "evaluation_criteria"
  | "compliance_matrix"
  | "submission_checklist"
  | "key_dates"
  | "risk_register"

const ANALYSIS_TABS: { value: AnalysisView; label: string }[] = [
  { value: "tender_overview", label: "Summary" },
  { value: "evaluation_criteria", label: "Evaluation Criteria" },
  { value: "compliance_matrix", label: "Compliance Matrix" },
  { value: "submission_checklist", label: "Submission Checklist" },
  { value: "key_dates", label: "Key Dates" },
  { value: "risk_register", label: "Risk Register" },
]

interface AnalysisSectionProps {
  bidId: string
  readOnly?: boolean
  /**
   * The views this host offers (R4: Qualify takes all six, including the
   * submission checklist). Defaults to all six. A `?view=` outside the list falls back
   * to `defaultView`.
   */
  views?: AnalysisView[]
  defaultView?: AnalysisView
  /** The host renders its own sub-nav, so the segmented control is hidden. */
  hideSubTabs?: boolean
}

export function AnalysisSection({
  bidId,
  readOnly,
  views,
  defaultView = "evaluation_criteria",
  hideSubTabs = false,
}: AnalysisSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const viewParam = searchParams.get("view")
  const isReviewSurface = viewParam === "addendum-review"
  const reviewRunId = searchParams.get("run")
  const offeredTabs = useMemo(
    () => (views ? ANALYSIS_TABS.filter((t) => views.includes(t.value)) : ANALYSIS_TABS),
    [views]
  )
  const activeView: AnalysisView = offeredTabs.some((t) => t.value === viewParam)
    ? (viewParam as AnalysisView)
    : defaultView

  // A citation preview opened from this tab docks beside the content on wide
  // screens instead of covering it (the modal self-hides on the same signal).
  // Registering as the dock host is what allows that docking at all.
  useDockHost('analysis')
  const readerDocked = useDockedAnalysisPreview()

  // Close any docked analysis preview when leaving the tab, so it doesn't
  // reappear as a full-screen modal on the next route (no dock host there).
  // Mirrors AssistantPage / TenderChat.
  useEffect(() => () => { usePreviewStore.getState().closePreview() }, [])

  const markCategoryViewed = useExtractionStore((s) => s.markCategoryViewed)
  // `tender_overview` has no extraction-category state (it isn't an
  // ExtractionCategory) — its selector must short-circuit to avoid indexing
  // `categories` with a key that doesn't exist.
  const hasUnseenForActive = useExtractionStore((s) =>
    activeView === "tender_overview"
      ? false
      : s.categories[activeView].hasUnseenCompletion
  )

  // Staleness banner — documents uploaded after a category was last analysed
  // mean that category no longer reflects the tender set.
  const categories = useExtractionStore((s) => s.categories)
  const fullReextract = useExtractionStore((s) => s.fullReextract)
  const documents = useDocumentsStore((s) => s.documents)
  const [isRerunning, setIsRerunning] = useState(false)

  const staleness = useMemo(() => {
    const documentCreatedAts = documents.map((d) => d.createdAt)
    const stale = computeStaleCategories(categories, documentCreatedAts)
    if (stale.length === 0) {
      return { stale, newDocCount: 0, allStale: false }
    }
    // N = documents newer than the OLDEST stale category's lastExtractedAt.
    const oldestStaleAt = stale
      .map((c) => categories[c].lastExtractedAt)
      .filter((v): v is string => v !== null)
      .reduce((a, b) => (a < b ? a : b))
    const newDocCount = documentCreatedAts.filter((d) => d > oldestStaleAt).length
    const populatedCount = (Object.keys(categories) as ExtractionCategory[]).filter(
      (c) => categories[c].state === "populated"
    ).length
    return { stale, newDocCount, allStale: stale.length === populatedCount }
  }, [categories, documents])

  // ── Tab collapse ──
  // A tab is tidied away only when its category ran to completion and returned
  // nothing — `isRanAndEmpty` is deliberately narrower than "looks empty", so a
  // category that is mid-extraction, recovering, or errored keeps its tab while
  // the app is still finding out. Collapsed tabs stay one click away because the
  // per-category view is the only place a run can be started again.
  //
  // "Summary" (tender_overview) is not an extraction category — it has no
  // CategoryState, so indexing `categories` with it would be undefined.
  // The active view never collapses, so a `?view=` deep link cannot land on a
  // hidden tab.
  const [revealCollapsed, setRevealCollapsed] = useState(false)

  const collapsedTabs = useMemo(
    () =>
      offeredTabs.filter(
        (tab) =>
          tab.value !== "tender_overview" &&
          tab.value !== activeView &&
          isRanAndEmpty(categories[tab.value])
      ).map((tab) => tab.value),
    [categories, activeView, offeredTabs]
  )

  const visibleTabs =
    revealCollapsed || collapsedTabs.length === 0
      ? offeredTabs
      : offeredTabs.filter((tab) => !collapsedTabs.includes(tab.value))

  const handleRerunStale = useCallback(async () => {
    setIsRerunning(true)
    try {
      for (const category of staleness.stale) {
        await fullReextract(bidId, category)
      }
    } finally {
      setIsRerunning(false)
    }
  }, [bidId, fullReextract, staleness.stale])

  // ── Addendum reconciliation banner (precedence: addendum beats stale) ──
  const reconRuns = useReconciliationStore((s) => s.runsByBid[bidId])
  const loadReconciliationRuns = useReconciliationStore((s) => s.loadRuns)
  const startReconcileRun = useReconciliationStore((s) => s.startRun)
  const getLatestRunForDocument = useReconciliationStore((s) => s.getLatestRunForDocument)
  const [isStartingAddendum, setIsStartingAddendum] = useState(false)

  useEffect(() => {
    void Promise.resolve(loadReconciliationRuns(bidId)).catch(() => {
      // Non-fatal — the addendum banner just won't show run state.
    })
  }, [bidId, loadReconciliationRuns])

  const addendumBanner = useMemo(() => {
    // Baseline addenda (uploaded before the bid had any analysis) are excluded:
    // they were part of the corpus the first analysis read, so there is no
    // earlier state for them to have changed. Prompting to "review what changed"
    // on a bid that has never been analysed asks the user to diff against nothing.
    const addenda = documents.filter(addendumNeedsReconciliation)
    if (addenda.length === 0) return null

    const runs = reconRuns ?? []

    // An active run (queued/running) belonging to any addendum in this bid.
    const activeRun =
      runs.find((r) => r.status === "queued" || r.status === "running") ?? null
    // A run awaiting review (PR 4 wires the surface; here it's an info pill).
    const reviewRun = runs.find((r) => r.status === "review") ?? null

    // Addenda with no run yet, or whose last run failed — the ones to reconcile.
    const unreconciled = addenda
      .filter((a) => {
        const run = getLatestRunForDocument(bidId, a.id)
        return !run || run.status === "failed"
      })
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

    if (unreconciled.length > 0) {
      return { kind: "prompt" as const, target: unreconciled[0]! }
    }
    if (activeRun) {
      const docName =
        documents.find((d) => d.id === activeRun.documentId)?.name ?? null
      return {
        kind: "progress" as const,
        run: activeRun,
        docName,
        categoryLabel: latestReconcileCategoryLabel(activeRun.counts),
      }
    }
    if (reviewRun) {
      return { kind: "review" as const, run: reviewRun }
    }
    return null
  }, [documents, reconRuns, bidId, getLatestRunForDocument])

  const handleReviewAddendum = useCallback(async () => {
    if (!addendumBanner || addendumBanner.kind !== "prompt") return
    setIsStartingAddendum(true)
    try {
      await startReconcileRun(bidId, addendumBanner.target.id)
    } catch (error) {
      toastApiError(error, "Failed to start reconciliation")
    } finally {
      setIsStartingAddendum(false)
    }
  }, [addendumBanner, bidId, startReconcileRun])

  // Clear the unseen-completion flag for the currently active sub-tab.
  // Re-runs if the flag flips true while the user is already viewing it (e.g. streaming
  // completes for the sub-tab currently open), so the chip immediately clears.
  useEffect(() => {
    // Don't consume the unseen flag while the review surface is open — the
    // sub-tab isn't actually being viewed, so its chip must survive the trip.
    if (hasUnseenForActive && !isReviewSurface && activeView !== "tender_overview") {
      markCategoryViewed(activeView)
    }
  }, [activeView, hasUnseenForActive, markCategoryViewed, isReviewSurface])

  const handleViewChange = useCallback(
    (view: string) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete("highlight")
      newParams.delete("run")
      newParams.set("view", view)
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Open the review surface for a run (from the "changes proposed" banner).
  const handleOpenReview = useCallback(
    (runId: string) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete("highlight")
      newParams.set("view", "addendum-review")
      newParams.set("run", runId)
      setSearchParams(newParams, { replace: false })
    },
    [searchParams, setSearchParams]
  )

  // Back out of the review surface → clear its query params, return to the tabs.
  const handleExitReview = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete("view")
    newParams.delete("run")
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  // A review surface without a run id is invalid — fall back to the default view.
  useEffect(() => {
    if (isReviewSurface && !reviewRunId) {
      handleExitReview()
    }
  }, [isReviewSurface, reviewRunId, handleExitReview])

  if (isReviewSurface && reviewRunId) {
    return (
      <div className="-mt-2">
        <AddendumReviewView
          bidId={bidId}
          runId={reviewRunId}
          onBack={handleExitReview}
          readOnly={readOnly}
        />
      </div>
    )
  }

  return (
    // Flex row so the docked reader sits beside the WHOLE Analysis tab — every
    // sub-tab gets it, and the content column keeps its own width.
    <div className="flex items-start gap-0 -mt-2">
      <div className="min-w-0 flex-1 space-y-3">
        {/* Banner precedence: an addendum state (prompt / progress / review)
            wins over the plain staleness banner (spec M7). */}
        {/* The three addendum states share this slot, so the accent has to tell
            them apart: uploaded and changes-ready want a decision (NOTICE_ACT),
            analysing does not (NOTICE_QUIET, accent on the spinner only). */}
        {addendumBanner ? (
          addendumBanner.kind === "prompt" ? (
            <div className={cn(NOTICE_ACT, 'flex items-start justify-between gap-3')}>
              <div className="flex items-start gap-2">
                <GradientSparkleIcon className="mt-0.5 h-4 w-4" />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Addendum uploaded — review what changed?</p>
                  <p className="text-meta truncate max-w-md">
                    {addendumBanner.target.name}
                  </p>
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-2 shrink-0">
                  {staleness.stale.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isRerunning}
                      onClick={() => void handleRerunStale()}
                    >
                      <RefreshCw className={cn("h-4 w-4 mr-1", isRerunning && "animate-spin")} />
                      Re-run analysis
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ai-ghost"
                    disabled={isStartingAddendum}
                    onClick={() => void handleReviewAddendum()}
                  >
                    {isStartingAddendum ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <GradientSparkleIcon className="h-4 w-4 mr-1" />
                    )}
                    Review changes
                  </Button>
                </div>
              )}
            </div>
          ) : addendumBanner.kind === "progress" ? (
            <div className={cn(NOTICE_QUIET, 'flex items-center gap-2')}>
              <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--brand))]" />
              <p className="text-sm font-medium">
                {addendumBanner.docName
                  ? `Analysing ${addendumBanner.docName}${
                      addendumBanner.categoryLabel ? ` — ${addendumBanner.categoryLabel}` : ""
                    }…`
                  : "Analysing addendum…"}
              </p>
            </div>
          ) : (
            <div className={cn(NOTICE_ACT, 'flex items-center justify-between gap-3')}>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Addendum changes proposed</p>
                <Badge variant="warning" className="text-[10px] whitespace-nowrap">
                  {addendumBanner.run.decisionCounts.pending} change
                  {addendumBanner.run.decisionCounts.pending === 1 ? "" : "s"} proposed
                </Badge>
              </div>
              <Button
                size="sm"
                variant="ai-ghost"
                onClick={() => handleOpenReview(addendumBanner.run.id)}
              >
                <GradientSparkleIcon className="h-4 w-4 mr-1" />
                Review changes
              </Button>
            </div>
          )
        ) : (
          !readOnly &&
          staleness.stale.length > 0 && (
            <div className="flex items-start justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
              <div className="flex items-start gap-2">
                <RefreshCw className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">
                    {staleness.newDocCount} document{staleness.newDocCount !== 1 ? "s" : ""} uploaded
                    since last analysis
                  </p>
                  {!staleness.allStale && (
                    <p className="text-meta">
                      Out of date:{" "}
                      {staleness.stale.map((c) => EXTRACTION_CATEGORY_LABELS[c]).join(", ")}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={isRerunning}
                onClick={() => void handleRerunStale()}
                className="shrink-0"
              >
                <RefreshCw className={cn("h-4 w-4 mr-1", isRerunning && "animate-spin")} />
                Re-run analysis
              </Button>
            </div>
          )
        )}

        {/* Sub-tabs */}
        {!hideSubTabs && (
        <Tabs value={activeView} onValueChange={handleViewChange}>
          {/* The disclosure control is a SIBLING of the tablist, not a child of
              it: a `role="tablist"` may only own tabs, so a bare button inside
              would be skipped by Radix's arrow-key roving focus and mis-announced
              by screen readers. The flex row keeps it on the same line, reading as
              a low-emphasis control beside the segmented track rather than a
              broken pill inside it — the `bg-muted` track belongs to TabsList
              alone. */}
          <div className="flex items-center gap-2">
            <TabsList className="bg-muted p-1 h-9 rounded-lg">
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {!revealCollapsed && collapsedTabs.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-meta"
                onClick={() => setRevealCollapsed(true)}
              >
                Nothing found ({collapsedTabs.length})
              </Button>
            )}
          </div>
        </Tabs>
        )}

        {/* Content.
            `key={bidId}` on every view: React Router reuses this element across
            `:bidId` changes, so without it a view stays mounted through a bid
            A → bid B navigation and carries its local state over — the coverage
            filter, the post-award toggle, the search box, the team-member list.
            None of that is stored per bid, so nothing on screen would say bid B
            is being narrowed by bid A's settings. */}
        {activeView === "tender_overview" && (
          <TenderOverviewView key={bidId} bidId={bidId} readOnly={readOnly ?? false} />
        )}
        {activeView === "evaluation_criteria" && (
          <EvaluationCriteriaExtraction key={bidId} bidId={bidId} readOnly={readOnly ?? false} />
        )}
        {activeView === "compliance_matrix" && (
          <ComplianceMatrixExtraction key={bidId} bidId={bidId} readOnly={readOnly ?? false} />
        )}
        {activeView === "submission_checklist" && (
          <SubmissionChecklistExtraction key={bidId} bidId={bidId} readOnly={readOnly ?? false} />
        )}
        {activeView === "key_dates" && (
          <KeyDatesExtraction key={bidId} bidId={bidId} readOnly={readOnly ?? false} />
        )}
        {activeView === "risk_register" && (
          <RiskRegisterExtraction key={bidId} bidId={bidId} readOnly={readOnly ?? false} />
        )}
      </div>

      {/* Docked reader. ChatDocumentPanel is h-full and owns its own width,
          resize handle and Esc-to-close — this wrapper only supplies height
          and stickiness. `xl:block` matches the hook's 1280px breakpoint.
          Analysis sizes the reader smaller than chat does: the content column
          also has to fit the compliance table plus the 400px requirement
          drawer, so a chat-width 820px reader would crush them. Separate
          storage key so a chat-sized persisted width can't leak in here. */}
      {readerDocked && (
        <div className="sticky top-0 hidden h-[calc(100vh-7rem)] shrink-0 xl:block">
          <ChatDocumentPanel
            defaultWidth={460}
            minWidth={360}
            maxWidth={700}
            storageKey="analysis-doc-panel-width"
          />
        </div>
      )}
    </div>
  )
}
